import { getAppType, i18next } from '@vmos2/core';
import get from 'lodash/get';
import has from 'lodash/has';
import identity from 'lodash/identity';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isFinite from 'lodash/isFinite';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';

const parsers = {
  booleanNumber: (value: string) => Boolean(+value),
  identity,
  json: (value: string, defaultValue: any = {}) => {
    try {
      return JSON.parse(value);
    } catch (e) {
      return defaultValue;
    }
  },
  // For settings that support booleans or JSON objects.
  jsonOrBooleanNumber: (value: string, defaultValue = {}) => {
    try {
      const parsedJSON = JSON.parse(value);

      if (typeof parsedJSON === 'number') {
        return { enabled: Boolean(+parsedJSON) };
      }

      return parsedJSON;
    } catch (e) {
      return defaultValue;
    }
  },
  number: (value: string) => +value,
  numberOrDefault: (defaultValue: number) => (value: string) => {
    const result = +value;

    return isFinite(result) ? result : defaultValue;
  },
};

type Setting = Record<string, string>;

type SettingsParser = (
  value: string,
  allSettings: Setting,
  storeSettings: Setting | Record<string, never>,
  tenantSettings: Setting
) => any;

const settingsParsers: Record<string, [string, SettingsParser]> = {
  a11yModeEnabled: ['kiosk.accessibilityMode', parsers.booleanNumber],
  accountRewardsSteps: [
    'account.rewards.steps',
    value =>
      parsers.json(value, [
        {
          description: 'Earn [xx] [slug] for every £1 you spend',
          icon: 'Card',
        },
        {
          description: `Once you've earned [x] [slug], you'll gain a reward`,
          icon: 'Diamond',
        },
        {
          description: 'Apply your [slug] at checkout to get a discount',
          icon: 'ShoppingCart',
        },
      ]),
  ],
  acteolIntegration: ['acteol.integration.public', value => parsers.json(value, null)],
  acteolOnRegistration: [
    'preorder.loyalty.earn',
    value => {
      const result = parsers.json(value);

      return parsers.booleanNumber(result.acteolOnRegistration);
    },
  ],
  additionalInformation: ['checkout.additionalInformation', parsers.json],
  adyenTimeout: ['preorder.adyen.timeout', value => parsers.number(value) || 120],
  allergensDisplayMode: ['displayAllergensAsText', value => (+value ? 'text' : 'icon')],
  allergensModalEnabled: ['allergens.disclaimer.enabled', parsers.booleanNumber],
  allowEditing: ['order.allowEditing', parsers.booleanNumber],
  appDownloadLink: ['app.downloadLink', parsers.json],
  appWebUrl: ['app.webUrl', parsers.identity],
  authAction: [
    'auth.action',
    value => {
      if (!includes(['register', 'login'], value)) {
        return null;
      }

      return `auth.${value}`;
    },
  ],
  authRedirection: ['auth.redirection', parsers.json],
  autoMealDealDetection: ['autoMealDealDetection', parsers.booleanNumber],
  bannerSessionTime: ['banner.session.time', parsers.numberOrDefault(60)],
  bundlesRecommendationHorizontal: ['bundles.recommendation.horizontal', value => parsers.json(value, {})],
  busyMode: ['busyMode.banner', parsers.json],
  cameraQrScannerElo: ['camera.qr.scanner.elo', parsers.json],
  cfCaptchaAccountPhoneVerification: [
    'cloudflareCaptcha.account.phoneVerification',
    value => parsers.json(value, null),
  ],
  cfCaptchaDiscountPhoneVerification: [
    'cloudflareCaptcha.discount.phoneVerification',
    value => parsers.json(value, null),
  ],
  cfCaptchaPointsRedeem: ['cloudflareCaptcha.points.redeem', value => parsers.json(value, null)],
  checkoutPhoneNumber: ['checkout.phone.number', value => parsers.json(value, {})],
  clickAndCollectAvailableDays: ['preorder.clickAndCollect.availableDays', parsers.numberOrDefault(1)],
  timeslotAvailableRange: ['preorder.clickAndCollect.timeslot.availableRange', parsers.numberOrDefault(0)],
  country: ['country', value => value || 'GB'],
  countryCode: ['country.code', value => value || 'GB'],
  createAccountConfirmEmail: ['createAccount.confirmEmail', value => parsers.json(value, { enabled: false })],
  currency: ['currency', value => parsers.json(value, { code: 'gbp', sign: '£' })],
  deliverooMenu: ['deliverooHop.menu.enabled', parsers.booleanNumber],
  deliveryAsapOrderEnabled: ['preorder.delivery.asap.enabled', parsers.numberOrDefault(0)],
  deliveryAsapOrderText: ['preorder.delivery.asap.text', value => value || ''],
  deliveryAvailableDays: ['preorder.delivery.availableDays', parsers.numberOrDefault(1)],
  deliveryDailyOrderLimit: ['preorder.delivery.dailyOrderLimit', parsers.numberOrDefault(0)],
  deliveryFee: ['preorder.delivery.fee', value => parsers.json(value, [])],
  deliveryOnly: ['preorder.delivery.only', parsers.booleanNumber],
  deliveryOnlyAsap: ['delivery.only.asap', value => (value ? parsers.json(value) : {})],
  dietsDisplayMode: ['displayDietsAsText', value => (+value ? 'text' : 'icon')],
  discountHideCode: ['discount.hide.code', parsers.json],
  discountsMultiFormat: ['discounts.multi.format', value => parsers.json(value, { online: false })],
  discountsMultipleApplication: [
    'discounts.multiple.application',
    value => parsers.json(value, { multipleAccountDiscounts: false }),
  ],
  displayAllergenFilter: ['allergen.displayFilter', parsers.numberOrDefault(0)],
  displayBirthDateField: ['user.birthday.field', parsers.json],
  displayCheapestFromPrice: ['displayCheapestFromPrice', parsers.booleanNumber],
  displayEnergyAsKJ: ['nutrition.displayEnergyAsKJ', parsers.booleanNumber],
  displayProductSearch: ['productSearch', parsers.booleanNumber],
  displayServingsLabel: ['nutrition.displayServingsLabel', parsers.booleanNumber],
  driverIntegration: ['driver.integration', parsers.json],
  emailVerification: ['email.verification', parsers.json],
  excludeUpsellEnergy: ['nutrition.excludeUpsellEnergy', parsers.booleanNumber],
  externalPages: ['externalPages', value => (value ? parsers.json(value) : { privacyUrl: null, termsUrl: null })],
  feedback: ['order.feedback', value => parsers.json(value) || {}],
  forceStockStatus: ['stockManagement.displayOnFrontend', parsers.numberOrDefault(0)],
  forceTimedMeals: ['timedMeal.displayOnMenu', parsers.numberOrDefault(0)],
  friendReferral: ['friend.referral', parsers.json],
  geocode: ['geocode', parsers.json],
  geolocationKey: ['geolocation.key', parsers.identity],
  giftCardsEnabled: ['order.payment.enableGiftCards', parsers.booleanNumber],
  googleMaps: ['googleMaps', parsers.json],
  groupCustomisableBundles: ['basket.groupCustomisableBundles', parsers.booleanNumber],
  gtm: ['google.tag.manager', parsers.identity],
  gtmAndroid: ['google.tag.manager.android', parsers.json],
  gtmIos: ['google.tag.manager.ios', parsers.json],
  guestCheckout: [
    'guest.checkout',
    value => {
      if (value === '1') {
        return { enabled: true };
      }

      return parsers.json(value, { enabled: false });
    },
  ],
  hideBundleCustomizationNutrition: ['bundle.customization.hideNutrition', parsers.booleanNumber],
  hideStores: ['hide.stores', parsers.booleanNumber],
  isLoyaltyPointsEnabled: ['preorder.loyalty.points.enabled', parsers.booleanNumber],
  nameFieldSetting: [
    'checkout.name.field',
    value => {
      const result = parsers.json(value, {});
      const appType = getAppType();
      const appResult = result[appType];

      // We want it to be enabled by default, so only disable it when it's explicitly set to false.
      if (appType === 'kiosk' && typeof appResult === 'undefined') {
        return true;
      }

      return appResult;
    },
  ],
  isPreAuth: ['adyen.preauthorisation.enabled', parsers.booleanNumber],
  kioskAllowAuth: ['kiosk.allow.auth', parsers.booleanNumber],
  kioskConfigurationPage: ['kiosk.configurationPage', parsers.json],
  kioskDiscountsEnabled: ['kiosk.discounts.enabled', parsers.booleanNumber],
  kioskEmailAddressDisabled: ['kiosk.emailAddress.disabled', parsers.numberOrDefault(0)],
  kioskLargeScreenSideNav: ['kiosk.largeScreen.sidenav', parsers.booleanNumber],
  kioskLoginTimeout: ['kiosk.login.timeout', value => (parsers.number(value) || 20) * 1000],
  kioskOptInPrintReceipt: ['kiosk.optInPrintReceipt', parsers.booleanNumber],
  kioskOrderConfirmationTimeout: ['kiosk.order-confirmation.timeout', value => parsers.number(value) || 10000],
  kioskPayAtCounter: ['kiosk.payAtCounter', parsers.json],
  kioskPermanentFooter: [
    'kiosk.permanentFooter',
    value => {
      const result = parsers.json(value, {});
      const accessibility = (isLargeKioskScreen: boolean) => result.accessibility && isLargeKioskScreen;

      return {
        ...result,
        accessibility,
        enabled: (isLargeKioskScreen: boolean) => accessibility(isLargeKioskScreen) || !!result.languageSelector,
      };
    },
  ],
  kioskPrintCustomerReceipt: ['kiosk.printCustomerReceipt', parsers.booleanNumber],
  kioskRemoteDebug: ['kiosk.remoteDebug', v => parsers.json(v, { enabled: false })],
  kioskSettingsPage: ['kiosk.settingsPage', value => parsers.json(value, null)],
  kioskShallowLoginEnabled: ['kiosk.shallowLogin.enabled', parsers.booleanNumber],
  kioskSplashScreen: ['kiosk.splash.screen', parsers.json],
  locale: ['locale', value => value || 'en-GB'],
  localeSwitcher: ['locale.switcher', parsers.booleanNumber],
  loyaltyEarnRules: ['loyalty.earnRules', parsers.json],
  loyaltyHighlightRewards: ['loyalty.highlightRewards', value => parsers.json(value, { enabled: false })],
  loyaltyOfflineCodes: [
    'loyalty.offline.codes',
    value => parsers.jsonOrBooleanNumber(value, { enabled: false, postClaimButtonLink: '' }),
  ],
  loyaltyPointsShopEnabled: ['loyalty.pointsShop.enabled', parsers.booleanNumber],
  loyaltyPointsUpdateTimeout: ['loyalty.points.update.timeout', parsers.numberOrDefault(4000)],
  loyaltyPosQR: ['loyalty.pos.qr', parsers.booleanNumber],
  loyaltyRewards: ['loyalty.rewards', parsers.json],
  loyaltyRewardsExternalLink: ['loyalty.rewards.externalLink', parsers.json],
  loyaltySignupAllowSkip: ['loyalty.signup.allowSkip', parsers.booleanNumber],
  loyaltyStampCard: ['loyalty.stampCard', value => parsers.json(value, { enabled: false, stampsPerPromo: 0 })],
  loyaltySubscription: ['loyalty.subscription', value => parsers.json(value, null)],
  loyaltyTierSystem: ['loyalty.tier.system', parsers.json],
  loyaltyWalletPass: ['loyalty.walletPass', parsers.json],
  mealBuilderHideHeader: ['mealBuilder.hideHeader', parsers.booleanNumber],
  mealBuilderRequiredTags: ['mealBuilder.requiredTags', parsers.booleanNumber],
  mealDealLinearFlow: ['mealDeal.linearFlow', parsers.booleanNumber],
  memberNumberEnabled: ['member.number.enabled', parsers.booleanNumber],
  menuAllowManualQuantityEntry: ['menu.allowManualQuantityEntry', parsers.json],
  menuAvailabilityInterval: ['menu.availability.interval', parsers.numberOrDefault(10)],
  menuHoursChannelStatus: ['menu.hours.channel.enabled', parsers.booleanNumber],
  menuUpsellModalHighLightMainMeal: ['menu.upsellModal.highlightMainMeal', parsers.booleanNumber],
  mobileAppsConfig: ['mobileApps.config', parsers.json],
  mobileMealSquareImage: ['mobile.meal.squareImage', parsers.booleanNumber],
  mobilePushNotificationsEnabled: ['mobile.push.notifications', parsers.booleanNumber],
  nativeAppForceUpdate: ['native.app.forceUpdate', parsers.json],
  nativeAppPaymentMethods: ['apps.payment.methods', parsers.json],
  neptingMerchantID: ['nepting.merchantId', parsers.identity],
  nutritionDisclaimerEnabled: ['nutrition.disclaimer.enabled', parsers.booleanNumber],
  nutritionLawText: ['nutrition.displayLawText', parsers.json],
  offlineModal: ['native.app.offline', parsers.json],
  onlinePayAtCounter: ['online.payAtCounter', parsers.json],
  opatTableNumber: ['opat.TableNumber', parsers.json],
  opatTakeaway: ['opat.allowTakeaway', parsers.booleanNumber],
  optionalTip: ['optional.tip', value => parsers.json(value, null)],
  orderConfirmationInstructions: [
    'orderConfirmation.instructions',
    value => {
      const settingInstruction = parsers.json(value, null);
      const app = getAppType();
      let instruction;

      try {
        instruction = get(settingInstruction, app, get(settingInstruction, 'default')).replace(
          /<a href=['‘"](.*?)['’"]>/g,
          (match: string, hrefLink: string) => {
            // target="_blank" == window.open("${hrefLink}", "_blank")
            return `<a href='javascript:void(0)' onclick='window.open("${hrefLink}", "_blank")'>`;
          }
        );
      } catch (e) {
        instruction = !includes(['opat', 'kiosk'], app)
          ? i18next.t(`orderConfirmationV2:${app === 'delivery' ? 'instructionsDelivery' : 'instructions'}`)
          : null;
      }

      return instruction;
    },
  ],
  orderPriceRestriction: [
    'order.price.restriction',
    value => {
      const restrictions = parsers.json(value);

      try {
        if (isEmpty(restrictions)) {
          return {};
        }

        const restrictionsApplyToAllApps = has(restrictions, 'minPrice') && has(restrictions, 'maxPrice');

        if (restrictionsApplyToAllApps) {
          return restrictions;
        }

        const appSpecificRestrictions = get(restrictions, getAppType());

        if (isEmpty(appSpecificRestrictions)) {
          return {};
        }

        return appSpecificRestrictions;
      } catch (e) {
        return {};
      }
    },
  ],
  orderTimeSlotValidation: ['order.timeSlot.validation', parsers.booleanNumber],
  payAtEnd: ['order.payAtEnd', parsers.json],
  paymentExternalAuthV2: ['payment.external.auth.v2', parsers.booleanNumber],
  phoneAllowedCountries: ['phone.allowedCountries', parsers.json],
  phonePrefix: ['phone.prefix', value => value || ''],
  phoneVerification: ['phone.verification', parsers.json],
  pickupDelivery: [
    'storeSelection.selectors',
    value => {
      try {
        const selectors = parsers.json(value);

        return !!selectors.find(
          (selector: { enabled: boolean, type: string }) => selector.type === 'online' && selector.enabled
        );
      } catch (e) {
        return false;
      }
    },
  ],
  placeOrderTimeout: ['place.order.timeout', parsers.numberOrDefault(15)],
  pointsPerItems: ['loyalty.points.per.items', parsers.json],
  preorderAvailableMenusLimit: ['preorder.available.menus.limit', parsers.numberOrDefault(3)],
  preorderCopyright: [
    'preorder.copyright',
    value => parsers.json(value, { link: 'https://vitamojo.com', name: 'Vita Mojo', text: 'Powered by' }),
  ],
  preorderCopyrightEnabled: ['preorder.copyright.enabled', (value: string = '1') => parsers.booleanNumber(value)],
  preorderDeliveryEnabled: ['preorder.delivery.enabled', parsers.booleanNumber],
  preorderDiscountsEnabled: ['preorder.discounts.enabled', parsers.booleanNumber],
  presetAddresses: ['delivery.preset.addresses', parsers.json],
  pusherOptions: ['paymentNotifications', parsers.identity],
  qrCodeAuth: ['qrcode.authentication', parsers.booleanNumber],
  qrCodeEnabled: ['qrcode.enabled', parsers.booleanNumber],
  qrCodeEnterCodeEnabled: ['qrcode.authentication.enter.code.enabled', parsers.booleanNumber],
  qrCodeScanDiscountEnabled: ['qrcode.scan.discount.enabled', parsers.booleanNumber],
  recaptcha: ['recaptcha', value => parsers.json(value, null)],
  receiptErrorTemplate: ['print.template.error', parsers.identity],
  receiptTemplate: ['print.template.customerReceipt', parsers.identity],
  referralCode: ['createAccount.referralCode', value => parsers.json(value) || {}],
  secretMenuTimeout: ['secretMenuTimeout', parsers.numberOrDefault(20)],
  showCurrencySign: ['show.currency.sign', value => (isNil(value) ? true : parsers.booleanNumber(value))],
  showCustomizations: ['basket.show.customizations', parsers.booleanNumber],
  showCustomizationsOrTrue: [
    'basket.show.customizations',
    value => (isNil(value) ? true : parsers.booleanNumber(value)),
  ],
  stockManagementLimitedQuantity: ['stockManagement.limitedQuantity', parsers.json],
  storeHasEatIn: [
    'hasEatIn',
    (value, allSettings, storeSettings) => {
      if (!!+get(storeSettings, ['preorder.delivery.only']) && getAppType() !== 'kiosk') {
        return false;
      }

      return parsers.booleanNumber(storeSettings.hasEatIn);
    },
  ],
  storeHasTableNumberInstruction: ['tableNumber.instruction.enabled', parsers.booleanNumber],
  storeSelectionAddress: ['storeSelection.address', parsers.json],
  storeSelectionKilometers: ['store.selection.kilometers', parsers.numberOrDefault(0)],
  storeShowDiningTypePage: ['kiosk.dining-type-page.enabled', parsers.booleanNumber],
  storeTableNumber: [
    'tableNumber.enabled',
    value => {
      try {
        const tableNumberEnabled = JSON.parse(value);

        if (isObject(tableNumberEnabled)) {
          return tableNumberEnabled;
        }

        return {
          eatIn: !!tableNumberEnabled,
        };
      } catch (e) {
        return {};
      }
    },
  ],
  storeTableNumberInstruction: ['tableNumber.instruction', parsers.identity],
  subwayMenu: [
    'subway.menu',
    (value, allSettings, storeSettings, tenantSettings) => parsers.json(tenantSettings?.['subway.menu']),
  ],
  tagsOff: ['hide.by.tags', value => parsers.json(value, [])],
  takeawayDefault: ['order.takeaway.default', parsers.booleanNumber],
  timeslotUnavailabilityBanner: [
    'timeslot.unavailabilityBanner.minutes',
    value => parsers.json(value, { enabled: false, minutes: 0 }),
  ],
  timezone: ['timezone', parsers.identity],
  userMarketingOptin: ['user.marketing.optin', parsers.json],
  useSharedPrint: ['kiosk.useSharedPrint', parsers.booleanNumber],
  websocketOptions: ['websocketOptions', value => parsers.json(value, null)],
};

export { parsers, settingsParsers };
